import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Auth, GoogleAuthProvider, signInWithPopup } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserDeserialize } from '../../shared/models/user/user.deserialize';
import { User } from '../../shared/models/user/user.model';
import { AuthSessionService } from './auth-session.service';
import { AuthStateService } from './auth-state.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authStateService = inject(AuthStateService);
  private readonly authSessionService = inject(AuthSessionService);
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  constructor(private httpClient: HttpClient) {}

  public initializeSession(): Observable<void> {
    return from(this.authSessionService.waitForInitialSession()).pipe(
      switchMap((user) => {
        if (!user) {
          return of(void 0);
        }
        return this.loadApplicationUser().pipe(
          tap(() => this.navigateToUserPage()),
          map(() => void 0),
          catchError((error) => this.handleInitialAuthenticationError(error)),
        );
      }),
      tap(() => this.authStateService.initialized.set(true)),
    );
  }

  public async login(): Promise<void> {
    try {
      await signInWithPopup(this.auth, new GoogleAuthProvider());
      await new Promise<void>((resolve, reject) => {
        this.loadApplicationUser().subscribe({
          next: () => {
            this.navigateToUserPage();
            resolve();
          },
          error: reject,
        });
      });
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 403) {
        await this.authSessionService.clearSession();
      }
      throw error;
    }
  }

  public async logout(): Promise<void> {
    await this.authSessionService.clearSession();
    window.location.href = 'https://www.enac.gov.it/';
  }

  public loadApplicationUser(): Observable<User> {
    return this.httpClient
      .get<{ result: User }>(`${environment.apiUrl}/users/me`)
      .pipe(
        map((response) => UserDeserialize.toUser(response.result)),
        tap((user) => this.authStateService.user.set(user)),
      );
  }

  private navigateToUserPage(): void {
    const destination = this.authStateService.isAdmin()
      ? '/admin-page'
      : '/user-page';
    if (this.router.url !== destination) {
      void this.router.navigate([destination]);
    }
  }

  private handleInitialAuthenticationError(error: unknown): Observable<void> {
    if (error instanceof HttpErrorResponse && error.status === 403) {
      return from(this.authSessionService.clearSession()).pipe(
        map(() => void 0),
      );
    }

    // Network and server errors do not invalidate a Firebase session. In a
    // mounted form this preserves the current user and all unsaved values.
    if (error instanceof HttpErrorResponse && error.status === 401) {
      this.authStateService.clear();
    }

    return of(void 0);
  }
}
